/**
 * TiptapEditorToolbar — static top toolbar with common formatting actions.
 * Opt-in: enable via `showToolbar` on TiptapMarkdownEditor. Bubble menus
 * cover the same actions on selection; the static toolbar adds discoverability
 * for new users editing markdown documents.
 */

import * as React from 'react'
import { useEditorState, type Editor } from '@tiptap/react'
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListChecks,
  Quote,
  FileCode,
  Minus,
  Table as TableIcon,
} from 'lucide-react'
import { cn } from '../../lib/utils'

interface ToolbarButtonProps {
  onClick: () => void
  isActive?: boolean
  disabled?: boolean
  title: string
  children: React.ReactNode
}

function ToolbarButton({ onClick, isActive, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'tiptap-toolbar-btn',
        isActive && 'is-active',
      )}
    >
      {children}
    </button>
  )
}

function ToolbarSeparator() {
  return <div className="tiptap-toolbar-separator" />
}

interface TiptapEditorToolbarProps {
  editor: Editor
}

export function TiptapEditorToolbar({ editor }: TiptapEditorToolbarProps) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      isBold: e.isActive('bold'),
      isItalic: e.isActive('italic'),
      isStrike: e.isActive('strike'),
      isCode: e.isActive('code'),
      isH1: e.isActive('heading', { level: 1 }),
      isH2: e.isActive('heading', { level: 2 }),
      isH3: e.isActive('heading', { level: 3 }),
      isBulletList: e.isActive('bulletList'),
      isOrderedList: e.isActive('orderedList'),
      isTaskList: e.isActive('taskList'),
      isBlockquote: e.isActive('blockquote'),
      isCodeBlock: e.isActive('codeBlock'),
    }),
  })

  const iconClass = 'w-4 h-4'

  return (
    <div className="tiptap-toolbar">
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={state.isBold}
        title="Bold (⌘B)"
      >
        <Bold className={iconClass} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={state.isItalic}
        title="Italic (⌘I)"
      >
        <Italic className={iconClass} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={state.isStrike}
        title="Strikethrough"
      >
        <Strikethrough className={iconClass} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        isActive={state.isCode}
        title="Inline code"
      >
        <Code className={iconClass} />
      </ToolbarButton>

      <ToolbarSeparator />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={state.isH1}
        title="Heading 1"
      >
        <Heading1 className={iconClass} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={state.isH2}
        title="Heading 2"
      >
        <Heading2 className={iconClass} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={state.isH3}
        title="Heading 3"
      >
        <Heading3 className={iconClass} />
      </ToolbarButton>

      <ToolbarSeparator />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={state.isBulletList}
        title="Bullet list"
      >
        <List className={iconClass} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={state.isOrderedList}
        title="Numbered list"
      >
        <ListOrdered className={iconClass} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleTaskList().run()}
        isActive={state.isTaskList}
        title="Task list"
      >
        <ListChecks className={iconClass} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={state.isBlockquote}
        title="Quote"
      >
        <Quote className={iconClass} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={state.isCodeBlock}
        title="Code block"
      >
        <FileCode className={iconClass} />
      </ToolbarButton>

      <ToolbarSeparator />

      <ToolbarButton
        onClick={() =>
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
        }
        title="Insert table"
      >
        <TableIcon className={iconClass} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Divider"
      >
        <Minus className={iconClass} />
      </ToolbarButton>
    </div>
  )
}
